import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy, OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  Boekingsregels,
  BokkzSalarisClient,
  Journaalpost,
  LoonJournaal, MemoriaalBoeking,
  WerkgeverEntity,
  WerknemerEntity,
} from '../services/api.bokkz.salaris';
import {BokkzWageDataService} from '../services/bokkz-wage-data-service';
import {BokkzWageLogic} from '../services/bokkz-wage-logic';
import {catchError, filter, finalize, map, Observable, shareReplay, Subscriber, tap, throwError} from 'rxjs';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  BokkzClient,
  ClientEntity,
  PdfRequest,
  PdfRequestOptie,
  PdfRequestSoort,
  PdfRequestType
} from "../services/api.bokkz.services";
import {HttpError, KolommenbalansService} from "../services/rekenclient.api";
import {BokkzAlgDataService} from "../services/bokkz-alg-data-service";

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-verloning',
  templateUrl: './verloning.component.html',
  imports: [
    SharedUiTabModule,
    SharedUiAlertModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule,
  ],
  styleUrl: './verloning.component.css',
})
export class VerloningComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public clientId = {} as string;
  @Input() public view = {} as string;
  @Input() public dialogVisible = false;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly bokkzSalarisClient = inject(BokkzSalarisClient);
  private readonly dataService = inject(BokkzWageDataService);
  private readonly logicService = inject(BokkzWageLogic);
  private readonly snelstartApiService = inject(KolommenbalansService);

  public werkgeverEntity = {} as WerkgeverEntity;
  public loonJournaal = {} as LoonJournaal;
  public selectedPeriode = {} as number;
  public selectedYear = {} as string;
  public result: Record<string, { omschrijving: string; bedragDebet: number; bedragCredit: number }> = {};
  public hasAdmin = false;
  public adminId = {} as string;
  public adminName = {} as string;
  public boekingsdatum = {} as string;

  readonly clientEntity$: Observable<ClientEntity> =
    this.algDataService.clientEntity$.pipe(
      filter((data): data is ClientEntity => !!data?.sstRelationId),
      tap(data => {
        if (data && data.adminEntities) {
          this.hasAdmin = data.adminEntities.some((admin) => admin.providerId === undefined);
          const adminEntity = data.adminEntities?.[0];
          if (adminEntity?.administrationId && adminEntity.name) {
            this.adminId = adminEntity.administrationId;
            this.adminName = adminEntity.name;
          }
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werkgeverEntity$: Observable<WerkgeverEntity> =
    this.dataService.werkgeverEntity$.pipe(
      filter((data): data is WerkgeverEntity => !!data?.id),
      tap(data => {
        this.werkgeverEntity = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedPeriode$: Observable<number> =
    this.dataService.selectedPeriode$.pipe(
      filter((data): data is number => !!data),
      tap(data => {
        this.loonjournaalFg.reset();
        this.resultFg.reset();
        this.selectedPeriode = data;
        if (this.dialogVisible) {
          this.logicService.postCalculationRequest(
            String(this.clientId),
            String(data),
            String(2026),
            this.werkgeverEntity,
          );
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werknemerList$: Observable<WerknemerEntity[]> =
    this.dataService.werknemerList$.pipe(
      filter((data): data is WerknemerEntity[] => !!data),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly loonJournaal$: Observable<LoonJournaal> =
    this.dataService.loonJournaal$.pipe(
      filter((data): data is LoonJournaal => !!data.nettoLonen),
      tap(data => {
        this.loonJournaal = data;
        this.loonjournaalFg.reset();
        this.resultFg.reset();
        this.loonjournaalFg.patchValue(data, {emitEvent:false});
        this.updateForm(data);
        this.calculateBedragDebetPerGrootboek(data);
        this.updateFormResults();
        if (this.selectedPeriode && this.selectedYear) {
          const year = Number(this.selectedYear)

          const lastDayOfMonth = new Date(year, this.selectedPeriode, 0);

          const day = String(lastDayOfMonth.getDate()).padStart(2, '0');
          const month = String(lastDayOfMonth.getMonth() + 1).padStart(2, '0');
          const formattedYear = lastDayOfMonth.getFullYear();

          this.boekingsdatum = `${formattedYear}-${month}-${day}`;
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public loonjournaalFg = new FormGroup({
    periode: new FormGroup({
      begindatum: new FormControl<Date | null>(null),
      einddatum: new FormControl<Date | null>(null),
    }),
    uitersteBetaaldatum: new FormControl<Date | null>(null),
    betalingskenmerk: new FormControl<string | null>(null),
    teBetalen: new FormControl<number | null>(null),
    nettoLonen: new FormArray<
      FormGroup<{
        grootboeknummer: FormControl<string | null>;
        omschrijving: FormControl<string | null>;
        bedragDebet: FormControl<number | null>;
        bedragCredit: FormControl<number | null>;
      }>
    >([]),
  });

  public resultFg = new FormGroup({
    results: new FormArray<
      FormGroup<{
        omschrijving: FormControl<string | null>,
        bedragDebet: FormControl<number | null>,
        bedragCredit: FormControl<number | null>,
      }>
    >([]),
    defaultOmschrijving: new FormControl<string | null>('Totaal'),
    defaultGrootboek: new FormControl<string | null>(''),
    totalBedragDebet: new FormControl<number | null>(null),
    totalBedragCredit: new FormControl<number | null>(null),
  });

  ngOnInit() {
    this.clientEntity$.subscribe();
    this.werkgeverEntity$.subscribe();
    this.selectedPeriode$.subscribe();
    this.loonJournaal$.subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dialogChange = changes['dialogVisible'];
    if (!dialogChange) {
      return;
    }
    if (this.dialogVisible) {
      this.logicService.postCalculationRequest(
        String(this.clientId),
        String(this.dataService.selectedPeriode.value),
        String(this.dataService.year.value),
        this.werkgeverEntity,
      );
    }
  }

  ngOnDestroy(): void {
    this.resultFg.reset();
    this.loonjournaalFg.reset();
  }

  public verzendLoonaangifte = ButtonConfiguration.create({
    onClick: () =>
      this.verzendLoonaangifteNaarBackend().pipe(
        tap(() => {
          this.toastService.showSuccessMessage(
            'De aangifte loonheffingen wordt verzonden.'
          );
        }),
        catchError((error) => {
          console.log('error', error);
          this.toastService.showErrorMessage(
            'Er is een probleem opgetreden bij het verzenden van de aangifte loonheffingen...'
          );
          return throwError(() => error);
        }),
        finalize(() => {
          this.dialogVisibleChange.emit(false);
        })
      ),
  });

  public calculateBedragDebetPerGrootboek(apiResponse: LoonJournaal): void {
    this.result = {};
    if (apiResponse.journaalpostenPerWerknemer !== undefined) {
      for (const werknemer of apiResponse.journaalpostenPerWerknemer) {
        if (werknemer.journaalposten !== undefined) {
          for (const post of werknemer.journaalposten) {
            if (post.grootboeknummer !== undefined && post.omschrijving !== undefined) {
              if (!this.result[post.grootboeknummer]) {
                this.result[post.grootboeknummer] = {
                  omschrijving: post.omschrijving,
                  bedragDebet: 0,
                  bedragCredit: 0,
                };
              }
              this.result[post.grootboeknummer].bedragDebet += post.bedragDebet || 0;
              this.result[post.grootboeknummer].bedragCredit += post.bedragCredit || 0;
              this.roundHalfEven(this.result[post.grootboeknummer].bedragDebet, 2);
              this.roundHalfEven(this.result[post.grootboeknummer].bedragCredit, 2);
            }
          }
        }
      }
    }
  }

  public totalAmountDebet(): any {
    const sumDebet = Object.values(this.result).reduce((sum, {bedragDebet}) => sum + bedragDebet, 0);
    this.roundHalfEven(sumDebet);
    return this.resultFg.patchValue({totalBedragDebet: sumDebet || 0});
  }

  private roundHalfEven(value: number, decimals = 2): number {
    const factor = Math.pow(10, decimals);
    const scaledValue = value * factor;
    const floor = Math.floor(scaledValue);
    const diff = scaledValue - floor;

    let rounded: number;
    if (diff < 0.5) {
      rounded = floor;
    } else if (diff > 0.5) {
      rounded = floor + 1;
    } else {
      // Exactly halfway: round to the nearest even
      rounded = floor % 2 === 0 ? floor : floor + 1;
    }

    return rounded / factor;
  }

  public totalAmountCredit(): any {
    const sumCredit = Object.values(this.result).reduce((sum, {bedragCredit}) => sum + bedragCredit, 0);
    this.roundHalfEven(sumCredit);
    return this.resultFg.patchValue({totalBedragCredit: sumCredit || 0});
  }

  get nettoLonen(): FormArray<FormGroup> {
    return this.loonjournaalFg.get('nettoLonen') as FormArray<FormGroup>;
  }

  get results(): FormArray {
    return this.resultFg.get('results') as FormArray;
  }

  public onSelectTab(index: number) {
    this.dataService.updateSelectedPeriode(index);
  }

  public createFormGroup(item?: Journaalpost): FormGroup {
    return new FormGroup({
      grootboeknummer: new FormControl<string | null>(item?.grootboeknummer ?? null),
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedragDebet: new FormControl<number | null>(item?.bedragDebet ?? null),
      bedragCredit: new FormControl<number | null>(item?.bedragCredit ?? null),
    });
  }

  private updateForm(loonJournaal: LoonJournaal): void {
    const lijst = loonJournaal.nettoLonen || [];
    const formArray = this.nettoLonen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i]);
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup);
      }
    }
  }

  private updateFormResults(): void {
    const lijst = this.result || [];
    const formArray = this.resultFg.get('results') as FormArray;

    Object.entries(lijst).forEach(([grootboeknummer, {omschrijving, bedragDebet, bedragCredit}], i) => {
      if (i < formArray.length) {
        // Existing form group, patch value
        const control = formArray.at(i) as FormGroup;
        control.patchValue({
          grootboeknummer,
          omschrijving,
          bedragDebet,
          bedragCredit,
        });
      } else {
        // New form group, create and push
        const formGroup = this.createFormGroup({
          grootboeknummer,
          omschrijving,
          bedragDebet,
          bedragCredit,
        });
        formArray.push(formGroup);
      }
    });
  }

  public selectedPeriodeSalaris() {
    const maanden = [
      '',
      'JANUARI',
      'FEBRUARI',
      'MAART',
      'APRIL',
      'MEI',
      'JUNI',
      'JULI',
      'AUGUSTUS',
      'SEPTEMBER',
      'OKTOBER',
      'NOVEMBER',
      'DECEMBER',
    ];
    return maanden[this.selectedPeriode] || '';
  }

  private verzendLoonaangifteNaarBackend(): Observable<void> {
    return this.bokkzSalarisClient.postOneEmployer(
      String(this.clientId),
      true,
      String(this.selectedPeriode),
      String(this.dataService.year.value),
      this.werkgeverEntity,
    ).pipe(
      map(() => void 0)
    );
  }

  public journalPost = ButtonConfiguration.create({
    onClick: () =>
      new Observable<void>((subscription) => {
        this.postJournaalPost(subscription).then(() => console.log('complete'));
      }),
  });

  public journalPostExporteren = ButtonConfiguration.create({
    onClick: () =>
      new Observable<void>((subscription) => {
        this.genereerMemoriaalRapport(subscription);
      }),
  });

  async genereerMemoriaalRapport(subscription: Subscriber<void>) {
    const administrationIdentifier = this.adminId;
    const pdf = {} as PdfRequest;
    pdf.memoriaalBoeking = await this.genereerMemoriaalBoeking(subscription, administrationIdentifier, false);
    if (!pdf.memoriaalBoeking) {
      pdf.memoriaalBoeking = {} as MemoriaalBoeking;
    }
    pdf.memoriaalBoeking.werkgever = this.werkgeverEntity.werkgeverGegevens!;
    pdf.optie = PdfRequestOptie.MemoriaalBoeking;
    pdf.type = PdfRequestType.LB;
    pdf.soort = PdfRequestSoort.Werkgever;
    pdf.loonPeriode = this.selectedPeriode;
    pdf.run = 1;
    pdf.concept = true;
    pdf.time = new Date().toISOString();
    const pdfAntwoord = this.bokkzClient.getPdfRapport(pdf);
    pdfAntwoord.subscribe({
      next: (pdfStringBase64) => {
        let datum = new Date();
        if (pdf.time) {
          datum = new Date(pdf.time);
        }
        const formattedDatum = new Intl.DateTimeFormat('nl-NL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(datum);

        let blob = {} as Blob;
        try {
          blob = this.dataURItoBlob(pdfStringBase64);
        } catch (error) {
          this.toastService.showErrorMessage('Document is niet beschikbaar  ' + error);
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `Loonaangifte ${this.selectedPeriodeSalaris()} ${this.werkgeverEntity.werkgeverGegevens?.naamWerkgever} (${formattedDatum}).pdf`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        this.toastService.showSuccessMessage('Het pdf-document is als download beschikbaar.');
        subscription.next();
        subscription.complete();
      },
      error: (error) => {
        console.log('error', error);
        this.toastService.showErrorMessage(
          'Het pdf-document is helaas niet beschikbaar. Dit kan verschillende oorzaken hebben. We hebben onze IT-afdeling hierover geïnformeerd. Probeer het later nog eens. Blijft het probleem zich voordoen? Neem dan contact op met onze Klantenservice. ',
        );
        subscription.error(error);
      },
    });
  }

  async postJournaalPost(subscription: Subscriber<void>) {
    const administrationIdentifier = this.adminId;
    const memoriaalBoeking = await this.genereerMemoriaalBoeking(subscription, administrationIdentifier, true);
    const test = this.snelstartApiService.postMemoriaalBoeking(administrationIdentifier, memoriaalBoeking);
    await test
      .then(() => {
        subscription.next();
        subscription.complete();
        this.toastService.showSuccessMessage('Journaalpost succesvol geboekt.');
        this.dialogVisibleChange.emit(false);
      })
      .catch((error: HttpError) => {
        console.log(JSON.stringify(error));
        const global = error.error?.errors?.global;
        if (global) {
          let message = '';
          global?.forEach((bericht) => {
            message += `${bericht.message},`;
          });
          this.toastService.showErrorMessage(message);
          // subscription.next();
          subscription.error(global);
        } else {
          // subscription.next();
          subscription.error(error);
          this.toastService.showErrorMessage('Memoriaalboeking niet gelukt');
        }
      });
  }

  private createBoekingsregels(result: Record<
                                 string,
                                 {
                                   omschrijving: string;
                                   bedragDebet: number;
                                   bedragCredit: number;
                                 }
                               >,
  ): Boekingsregels[] {
    const boekingsregels: Boekingsregels[] = [];

    // Iterate over each grootboeknummer in the result
    for (const grootboeknummer in result) {
      const {omschrijving, bedragDebet, bedragCredit} = result[grootboeknummer];

      // Create the Boekingsregels object for each grootboeknummer
      const boekingsregel: Boekingsregels = {
        omschrijving: omschrijving,
        grootboekId: grootboeknummer,
        kostenplaatsPublicIdentifier: undefined,
        debet: this.roundHalfEven(bedragDebet, 2),
        credit: this.roundHalfEven(bedragCredit, 2),
        btwSoortDto: undefined,
        btwPercentage: undefined,
        factuurId: undefined,
        factuurNummer: undefined,
      };

      boekingsregels.push(boekingsregel);
    }
    return boekingsregels;
  }

  public genereerPdfNettoLonen() {
    const pdf = {} as PdfRequest;

    if (!this.loonJournaal?.nettoLonen || this.loonJournaal.nettoLonen.length === 0) {
      this.toastService.showErrorMessage('Er zijn geen netto lonen beschikbaar om te exporteren.');
      return;
    }

    pdf.type = PdfRequestType.LB;
    pdf.soort = PdfRequestSoort.Werkgever;
    pdf.optie = PdfRequestOptie.NettoLonen;
    pdf.concept = false;
    pdf.time = new Date().toISOString();
    pdf.loonPeriode = this.selectedPeriode;
    pdf.loonJaar = Number(this.selectedYear);
    pdf.werkgever = this.werkgeverEntity.werkgeverGegevens;

    pdf.nettoLonenOverzicht = {
      uitersteBetaaldatum: this.loonJournaal.uitersteBetaaldatum as unknown as string,
      betalingskenmerk: this.loonJournaal.betalingskenmerk,
      nettoLonen: (this.loonJournaal.nettoLonen ?? []).map((regel) => ({
        omschrijving: regel.omschrijving,
        iban: regel.grootboeknummer,
        bedrag: regel.bedragDebet,
      })),
    };

    const pdfAntwoord = this.bokkzClient.getPdfRapport(pdf);

    pdfAntwoord.subscribe({
      next: (pdfStringBase64) => {
        const datum = new Date(pdf.time!);
        const formattedDatum = new Intl.DateTimeFormat('nl-NL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(datum);

        let blob = {} as Blob;
        try {
          blob = this.dataURItoBlob(pdfStringBase64);
        } catch (error) {
          this.toastService.showErrorMessage('Document is niet beschikbaar ' + error);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download =
          'Overzicht netto lonen ' +
          this.selectedPeriodeSalaris() +
          ' ' +
          this.selectedYear +
          ' - ' +
          (this.werkgeverEntity.werkgeverGegevens?.naamWerkgever ?? '') +
          ' (' +
          formattedDatum +
          ').pdf';

        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.toastService.showSuccessMessage('Het pdf-document is als download beschikbaar.');
      },
      error: (error) => {
        console.log('error', error);
        this.toastService.showErrorMessage(
          'Het pdf-document is helaas niet beschikbaar. Dit kan verschillende oorzaken hebben. We hebben onze IT-afdeling hierover geïnformeerd. Probeer het later nog eens. Blijft het probleem zich voordoen? Neem dan contact op met onze Klantenservice.'
        );
      }
    });
  }

  private async genereerMemoriaalBoeking(subscription: Subscriber<void>, administrationIdentifier: string, updateBoeking: boolean) {
    const boekingsregels = this.createBoekingsregels(this.result);
    let boekingsdatum = {} as string
    try {
      boekingsdatum = new Date(this.boekingsdatum).toISOString();
    } catch (error) {
      console.log('error', this.boekingsdatum);
      subscription.next();
      subscription.complete();
      throw new Error('Ongeldige datum!');
    }
    if (boekingsdatum) {
      if (updateBoeking) {
        const dagboeken = await this.snelstartApiService.getDagboekId(administrationIdentifier);
        const memoriaalDagboek = dagboeken?.find((d) => d.dagboekType === 'Memoriaal');
        if (!memoriaalDagboek) {
          subscription.next();
          subscription.complete();
          throw new Error('Dagboektypes niet gevonden!');
        }
        const grootboekenIds = await this.snelstartApiService.getGrootboekenIds(administrationIdentifier);
        if (!grootboekenIds?.result) {
          subscription.next();
          subscription.complete();
          throw new Error('Grootboektypes niet gevonden!');
        }
        const updatedBoekingsregels = boekingsregels.map((boekingsregel) => {
          if (!boekingsregel.grootboekId) return boekingsregel;
          const matchedGrootboek = grootboekenIds.result.find((gb) => gb.number.toString() === boekingsregel.grootboekId);
          if (matchedGrootboek) {
            return {
              ...boekingsregel,
              grootboekId: matchedGrootboek.publicIdentifier,
            };
          }
          return boekingsregel;
        });
        const dagboekId = memoriaalDagboek.publicIdentifier;
        if (boekingsdatum) {
          return {
            boekingsdatum: new Date(this.boekingsdatum).toISOString(),
            omschrijving: 'Loonjournaalpost ' + this.selectedPeriodeSalaris().toLowerCase(),
            dagboekId: dagboekId,
            debet: this.roundHalfEven(this.resultFg.get('totalBedragDebet')?.getRawValue(), 2),
            credit: this.roundHalfEven(this.resultFg.get('totalBedragCredit')?.getRawValue(), 2),
            markering: false,
            boekstuk: '',
            boekingsregels: updatedBoekingsregels,
          } as MemoriaalBoeking;
        }
      } else {
        return {
          boekingsdatum: new Date(this.boekingsdatum).toISOString(),
          omschrijving: 'Loonjournaalpost ' + this.selectedPeriodeSalaris().toLowerCase(),
          dagboekId: '',
          debet: this.roundHalfEven(this.resultFg.get('totalBedragDebet')?.getRawValue(), 2),
          credit: this.roundHalfEven(this.resultFg.get('totalBedragCredit')?.getRawValue(), 2),
          markering: false,
          boekstuk: '',
          boekingsregels: boekingsregels,
        } as MemoriaalBoeking;
      }
    }
    subscription.next();
    subscription.error();
    throw new Error('Boekingsdatum niet gevonden!');
  }

  public dataURItoBlob(dataURI: string) {
    if (dataURI != null) {
      const byteString = atob(dataURI.replace(/"/g, ''));
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      return new Blob([int8Array], {type: 'application/pdf'});
    } else throw 'No dataobject';
  }

}
